import { Component, ViewChild } from '@angular/core';
import { MatIcon } from "@angular/material/icon";
import { MatButton, MatIconButton } from "@angular/material/button";
import { UploadFilesService } from '../../../services/upload-files.service';
import { NgIf, NgForOf } from '@angular/common';
import { MatProgressBar } from "@angular/material/progress-bar";
import { HttpEventType } from '@angular/common/http';
import { firstValueFrom, forkJoin, map, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { openSnackBar } from '../../../helpers/genericFunctions';
import { CustomFile } from '../../../interfaces/custom-file';

@Component({
  selector: 'app-upload-files',
  standalone: true,
  imports: [MatIcon, MatButton, NgIf, NgForOf, MatIconButton, MatProgressBar],
  templateUrl: './upload-files.component.html',
  styleUrl: './upload-files.component.scss'
})
export class UploadFilesComponent {



  constructor(
    private uploadFilesService: UploadFilesService,
    private snackBar: MatSnackBar
  ) {

  }

  files: CustomFile[] = []
  acceptedFileTypes = ['.doc', '.docx', '.pdf', '.csv', '.xlsx', 'image/*', '.txt'];
  ngOnInit(): void {

  }


  onFileSelected($event: Event) {
    const fileList = ($event.target as HTMLInputElement).files;
    if (!fileList) return;
    this.onDropFiles(fileList);
  }

  getExtension(filename: string): string {
    return '.' + filename.split('.').pop()!.toLowerCase();
  }


  showMessagesSequentially(messages: string[]) {
    if (!messages.length) return;
    let chain: Promise<void> = Promise.resolve();
    messages.forEach(msg => {
      chain = chain.then(async () => {
        const ref = this.snackBar.open(msg, "Dismiss", { duration: 5000 });
        await firstValueFrom(ref.afterDismissed());
        return;
      });
    });
  }


  dragCounter = 0;
  dragOver = false;

  onDragOver(event: DragEvent) {
    event.preventDefault(); // important to allow drop
    this.dragCounter++;
    this.dragOver = true;
  }

onDragLeave(event: DragEvent) {
  event.preventDefault();

  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const x = event.clientX;
  const y = event.clientY;

  // Only consider leaving if cursor is actually outside the container
  if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
    this.dragCounter = 0;
    this.dragOver = false;
  }
}

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragCounter = 0;
    this.dragOver = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.onDropFiles(files);
    }
  }

  onDropFiles(files: FileList) {


    const Files = Array.from(files);
    // remove file types that aren't allowed
    let accepted = Files.filter(file => {
      const ext = this.getExtension(file.name);
      const mime = file.type;

      return (
        this.acceptedFileTypes.includes(ext) ||
        (mime.startsWith('image/') && this.acceptedFileTypes.includes('image/*'))
      );
    });

    // Remove duplicates (based on filename)
    const existingFileNames = new Set(this.files.map(f => f.file.name));
    const uniqueAccepted = accepted.filter(file => {
      if (existingFileNames.has(file.name)) {
        return false; // duplicate
      }
      existingFileNames.add(file.name);
      return true;
    });

    // rejected because of filetype needs to be displayed
    const rejected = Files.filter(file => !accepted.includes(file));
    const messages = rejected.map(file => `${file.name} type is not accepted`);


    this.showMessagesSequentially(messages);
    rejected.forEach(file => {
      openSnackBar(`${file.name} type is not accepted`, 5000, this.snackBar);
    });

    const customFiles: CustomFile[] = uniqueAccepted.map(file => ({
      file,
      isUploading: false,
      progress: 0
    }));

    customFiles.forEach(file => {
      this.files.push(file);
    });

  }


  onFileUpload(ticket: any): Observable<any> {

    const uploads$ = this.files.map(file => {

      const cancel$ = new Subject<void>();
      file.cancel$ = cancel$;
      file.isUploading = true;

      return this.uploadFilesService.uploadFile(file.file, ticket.id, ticket.pct_initialdescription.id).pipe(
        takeUntil(cancel$), // allows us to unsubscribe if cancelled
        map(event => {
          if (event.type === HttpEventType.UploadProgress) {
            const percentage = Math.round((event.loaded / (event.total ?? 1)) * 100);
            file.progress = percentage;
          }

          if (event.type === HttpEventType.Response) {
            file.isUploading = false;
            file.uploaded = true;
            return { file, success: true };
          }

          return null;
        })
      );
    });

    // Complete when all uploads finish
    return forkJoin(uploads$).pipe(
      map(results => results.filter(r => r !== null)) // remove cancelled files
    );
  }




  onCancel($event: Event, file: CustomFile) {
    // Trigger cancellation
    file.cancel$?.next();
    file.cancel$?.complete();

    // Remove from list
    this.files = this.files.filter(f => f !== file);
  }
}